import {
  ArrowDownIcon,
  ArrowUpIcon,
  BarsArrowDownIcon,
  BoltIcon,
  BoltSlashIcon,
  CheckIcon,
  DocumentTextIcon,
  EyeIcon,
  EyeSlashIcon,
  LinkIcon,
  PencilSquareIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  TrashIcon,
  UsersIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PageOrderModal } from 'components/Modals/PageOrder'
import { SaveChanges } from 'components/SaveChanges'
import {
  customOptionLabels,
  editableFieldOptions,
  FieldOrigin,
  ILoanFieldPos,
  InputType,
  IVisibleProp,
  loanCustomRangeOptionFields,
  loanCustomSelectOptionFields,
  LoanFieldProps,
} from 'config'
import { hasAnyBorrowerRequired } from 'config/borrowerFieldRequired.util'
import {
  canAddNewField,
  canHaveSection,
  getVisibleLogics2Text,
  InputTypeOfLoans,
} from 'config/loan.input.fields.constants'
import { ILoanOriginSection, isCustomOrigin } from 'config/loan.input.visibility.type'
import { accountTypeWithAllBorrower } from 'pages/LoanApplication/config'
import React, { useEffect, useMemo, useState } from 'react'
import { Prompt } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, Section, Tooltip } from 'stories/components'
import { confirm, convertToSanitize, fillObject, prompt, removeComma, removeDuplicates } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { actionTypeTitles } from '../LoanSubmission/types'
import { BaseAdditionalInputs } from './AdditionalInputs'
import { CoborrowerRequiredModal } from './CoborrowerRequiredModal'
import { CustomFieldPreview } from './CustomFieldPreview'
import {
  addCustomInputs,
  canAddLinkedFieldOrigin,
  canControlSection,
  canEditField,
  extendedFieldOriginOptions,
  getInputs,
  hasCoborrowerRequiredProps,
  hasRequiredProps,
  visibilitySections,
} from './inputs'
import { OriginatorConfigModal } from './OriginatorConfigModal'
import { TemplateContentModal } from './TemplateContentModal'
import { convertSelectRangeOptionsToMap, setLoanFieldDefaultSection } from './utils'
import { VisiblePropertyModal } from './VisiblePropertyModal/VisiblePropertyModal'
import { VisibleSectionModal } from './VisibleSectionModal'

export const defaultAccessAccountTypes = () => fillObject(Object.keys(accountTypeWithAllBorrower()), true)
export const LoanFieldsProperties = (props: { setChanged?: Function }) => {
  const [isLoading, setLoading] = useState(true)
  const [data, setData] = useState<LoanFieldProps>()
  const [inputs, setInputs] = useState<InputTypeOfLoans>()
  const [isChanged, _setChanged] = useState<boolean>()
  const [selectedField, setSelectedField] = useState<ILoanFieldPos | null>(null)
  const [editTitleID, setEditTitleId] = useState<FieldOrigin | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [modalFieldOrigin, setModalFieldOrigin] = useState<FieldOrigin | null>(null)
  const [orderPageIndex, setOrderPageIndex] = useState<{ fieldOrigin: FieldOrigin; sectionId: number } | null>(null)
  const [showOrderModal, setOrderModal] = useState(false)
  const [coborrowerFieldModal, setCoborrowerFieldModal] = useState<{
    fieldOrigin: FieldOrigin
    fieldKey: string
    input: InputType
  } | null>(null)
  const [templateContentModal, setTemplateContentModal] = useState<{
    fieldOrigin: 'sign' | 'credit'
    fieldKey: string
  } | null>(null)
  const [originatorConfigModal, setOriginatorConfigModal] = useState(false)
  const [actionTypeOptions, setActionTypeOptions] = useState<Record<string, string>>({})

  useEffect(() => {
    getAdminConfig('loanActions')
      .then(({ labels }: any) => {
        const options: Record<string, string> = {}
        Object.keys(labels || {}).forEach((key) => {
          options[key] = (actionTypeTitles as any)[key] || key
        })
        setActionTypeOptions(options)
      })
      .catch(() => setActionTypeOptions({}))
  }, [])

  useEffect(() => {
    getAdminConfig('loanFieldProperties')
      .then((data) => {
        const newData = setLoanFieldDefaultSection(data)
        setData(newData)
        setInputs(getInputs(newData, false))
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!data || !inputs) return

    const newInputs = cloneDeep(inputs)
    addCustomInputs(newInputs, data)
    setInputs(newInputs)
    if (isChanged === undefined) setChanged(false)
    else !isChanged && setChanged(true)
  }, [data])

  const customTitle = (fieldOrigin: FieldOrigin) => {
    let title = (data as any)?.customTitle?.[fieldOrigin] || ''

    if (!title && isCustomOrigin(fieldOrigin)) title = extendedFieldOriginOptions[FieldOrigin.CustomInformation]

    return title || extendedFieldOriginOptions[fieldOrigin]
  }

  const pageTitlesOptions = useMemo(() => {
    const values: Record<string, string> = {}
    data?.order
      ?.filter((v) => v != FieldOrigin.LoanStructure)
      .forEach((fieldOrigin) => (values[fieldOrigin] = customTitle(fieldOrigin)))
    return values
  }, [data])

  const setChanged = (value: boolean) => {
    _setChanged(value)
    props.setChanged && props.setChanged(value)
  }

  const selectedFieldData = useMemo(() => {
    if (!selectedField || !data) return null
    if (!data[selectedField.fieldOrigin]) return {}
    return data[selectedField.fieldOrigin][selectedField.fieldKey] || {}
  }, [selectedField])

  const orderingSectionInputs = useMemo(() => {
    if (!orderPageIndex || !inputs) return []
    const { fieldOrigin, sectionId } = orderPageIndex
    const section = data?.sections?.[fieldOrigin]?.find((v) => v.id == sectionId)
    if (!section) return []

    return section.fields.map((fieldKey) => ({
      fieldKey,
      ...inputs[fieldOrigin][fieldKey],
    }))
  }, [orderPageIndex])

  const fieldTitles = useMemo(() => {
    if (!data || !inputs) return {}

    const result: Record<string, any> = {}
    const fieldOrigins = removeDuplicates([FieldOrigin.LoanStructure, ...(data?.order || [])])
    fieldOrigins.forEach((_fieldOrigin) => {
      const fieldOrigin = _fieldOrigin as FieldOrigin
      const fields = data[fieldOrigin]
      if (!fields) return

      const titles: Record<string, string> = {}
      const title = customTitle(fieldOrigin)
      if (!title) return

      Object.keys(fields).forEach((key) => {
        const title = fields[key].title || inputs[fieldOrigin]?.[key]?.title
        if (!title) return
        titles[key] = title
      })

      if (!Object.keys(titles).length) return
      result[fieldOrigin] = {
        title,
        fields: titles,
      }
    })

    return result
  }, [data])

  if (!inputs) return <LayoutLoading show />

  const isShowVisibleSection = (fieldOrigin: FieldOrigin) =>
    Object.keys(visibilitySections).includes(fieldOrigin) || isCustomOrigin(fieldOrigin)

  const getPageVisibleProps = (fieldOrigin: FieldOrigin) => ({
    accountTypes: data?.properties?.[fieldOrigin]?.accountTypes || defaultAccessAccountTypes(),
    ...(data?.properties?.[fieldOrigin] || {}),
  })

  const isVisibleSection = (fieldOrigin: FieldOrigin) =>
    data && ((data as any)['visibility'] || {})[fieldOrigin] !== false

  const onVisibleSection = (fieldOrigin: FieldOrigin) => {
    if (!data) return
    const newData = cloneDeep(data) as any
    if (!newData['visibility']) newData['visibility'] = {}
    newData['visibility'][fieldOrigin] = !isVisibleSection(fieldOrigin)
    setData(newData)
  }

  const onChange = (
    fieldOrigin: FieldOrigin,
    fieldKey: string,
    subKey: keyof IVisibleProp,
    value: any,
    input: InputType,
  ) => {
    if (!data) return
    if (!data[fieldOrigin]) data[fieldOrigin] = {}
    const temp = cloneDeep(data)
    if (!temp[fieldOrigin]) temp[fieldOrigin] = {}
    if (!temp[fieldOrigin][fieldKey]) temp[fieldOrigin][fieldKey] = {} as any

    const defaultValue = (temp[fieldOrigin][fieldKey] as any).defaultValue
    if (subKey == 'visible' && input.required && !value && ['', undefined].includes(defaultValue)) {
      toast('Default value is required', { type: 'warning' })
      return
    }

    if (
      ['number', 'thousandSep', 'thousandSepNoDecimal'].includes(input.type as any) &&
      subKey == 'defaultValue' &&
      value != ''
    ) {
      value = removeComma(value)
    }
    ;(temp[fieldOrigin][fieldKey] as any)[subKey] = value
    setData(temp)
  }

  const onCoborrowerRequiredModalSave = (fieldOrigin: FieldOrigin, fieldKey: string, next: IVisibleProp) => {
    if (!data) return
    const temp = cloneDeep(data) as any
    if (!temp[fieldOrigin][fieldKey]) temp[fieldOrigin][fieldKey] = {}
    Object.assign(temp[fieldOrigin][fieldKey], next)
    setData(temp)
    setCoborrowerFieldModal(null)
  }

  const onSaveTemplateContent = (value: Record<string, any>) => {
    if (!data || !templateContentModal) return
    const { fieldOrigin, fieldKey } = templateContentModal
    const temp = cloneDeep(data) as any
    if (!temp[fieldOrigin]) temp[fieldOrigin] = {}
    if (!temp[fieldOrigin][fieldKey]) temp[fieldOrigin][fieldKey] = {}
    temp[fieldOrigin][fieldKey].defaultValue = value
    setData(temp)
    setTemplateContentModal(null)
  }

  const onSaveOriginatorConfig = (value: Record<string, any>) => {
    if (!data) return
    const temp = cloneDeep(data) as any
    if (!temp.sign) temp.sign = {}
    if (!temp.sign.originatorInformation) temp.sign.originatorInformation = {}
    temp.sign.originatorInformation = {
      ...temp.sign.originatorInformation,
      title: value.title || 'Loan Originator Information',
      defaultValue: value,
    }
    setData(temp)
    setOriginatorConfigModal(false)
  }

  const onShowProperty = (fieldOrigin: FieldOrigin, fieldKey: string, input: InputType) => {
    setSelectedField({ fieldOrigin, fieldKey, title: input.title })
  }

  const onAddNewPage = () => {
    if (!data) return

    const newFieldOrigin = `${FieldOrigin.CustomInformation}-${Date.now()}`
    const newData = cloneDeep(data)
    newData[newFieldOrigin] = {}
    newData.order!.push(newFieldOrigin as any)
    setData(newData)
  }

  const onSubmit = async () => {
    if (!data) return
    setLoading(true)
    setAdminConfig('loanFieldProperties', data)
      .then(() => toast('Loan Fields Configuration has been saved.', { type: 'info' }))
      .finally(() => {
        setLoading(false)
        setChanged(false)
      })
  }

  const onEditTitle = (fieldOrigin: FieldOrigin) => {
    setEditTitleId(fieldOrigin)
    setEditTitle(customTitle(fieldOrigin))
  }

  const onCloseEdit = () => {
    setEditTitle('')
    setEditTitleId(null)
  }

  const onOkEdit = () => {
    if (!data || !editTitleID) return
    if (!editTitle) {
      toast('Title is required', { type: 'warning' })
      return
    }
    const newData = cloneDeep(data) as any
    if (!newData['customTitle']) newData['customTitle'] = {}
    newData['customTitle'][editTitleID] = editTitle
    setData(newData)
    setEditTitle('')
    setEditTitleId(null)
  }

  const onRemoveCustomInput = async (fieldOrigin: FieldOrigin, fieldKey: string) => {
    if (!data) return
    const isContinue = await confirm('Are you sure you want to remove this item?')
    if (!isContinue) return
    const newData = cloneDeep(data) as any

    delete newData[fieldOrigin][fieldKey]
    const newInputs = cloneDeep(inputs)
    delete newInputs[fieldOrigin][fieldKey]
    setInputs(newInputs)

    newData.sections?.[fieldOrigin]?.forEach((v: ILoanOriginSection) => {
      if (v.fields.includes(fieldKey)) v.fields.splice(v.fields.indexOf(fieldKey), 1)
    })
    setData(newData)
  }

  const onShowAccountModal = (fieldOrigin: FieldOrigin) => {
    setModalFieldOrigin(fieldOrigin)
  }

  const onRemoveOrigin = async (fieldOrigin: FieldOrigin) => {
    if (!data) return

    const isContinue = await confirm('Are you sure you want to remove this page?')
    if (!isContinue) return

    const newData = cloneDeep(data)
    delete newData[fieldOrigin]
    if (newData.sections && newData.sections[fieldOrigin]) delete newData.sections[fieldOrigin]
    if (newData.order && newData.order.includes(fieldOrigin))
      newData.order.splice(newData.order.indexOf(fieldOrigin), 1)
    setData(newData)
  }

  const onCloseAccountModal = () => {
    setModalFieldOrigin(null)
  }

  const onOkAccountModal = (sectionProps: Record<string, any>) => {
    if (!data || !modalFieldOrigin) return

    const newData = cloneDeep(data) as any
    if (!newData['properties']) newData['properties'] = {}
    newData['properties'][modalFieldOrigin] = {
      ...(newData['properties'][modalFieldOrigin] || {}),
      ...sectionProps,
    }

    setData(newData)
    setModalFieldOrigin(null)
  }

  const onCloseVisiblePropertyModal = (newItem?: IVisibleProp) => {
    if (!selectedField || !selectedFieldData) return

    setSelectedField(null)
    if (!newItem || !data) return

    let { fieldOrigin, fieldKey } = selectedField
    const newData = cloneDeep(data)
    if (!newData[fieldOrigin]) newData[fieldOrigin] = {}

    if (!fieldKey) {
      // ActionButton fields always use a numeric key so the backend
      // (isCustomFieldKey) routes clicks into loan.customInformation and the
      // `loan.field.updated` history event fires on click / reset.
      const needCustomKey = ['sign'].includes(fieldOrigin)
      fieldKey = !isCustomOrigin(fieldOrigin) || needCustomKey ? `${Date.now()}` : convertToSanitize(newItem.title!)
    }

    newData[fieldOrigin][fieldKey] = newItem

    const sections = newData.sections?.[fieldOrigin]

    if (selectedFieldData.section != newItem.section) {
      if (selectedFieldData.section && sections) {
        const index = sections.findIndex((v) => `${v.id}` == `${selectedFieldData.section}`)
        if (index != -1) {
          const sIndex = sections[index].fields.indexOf(fieldKey)
          sIndex != -1 && sections[index].fields.splice(sIndex, 1)
        }
      }
      if (newItem.section && sections) {
        const index = sections.findIndex((v) => `${v.id}` == `${newItem.section}`)
        const sIndex = index != -1 ? sections[index].fields.indexOf(fieldKey) : 0
        index != -1 && sIndex == -1 && sections[index].fields.push(fieldKey)
      }
    }

    setData(newData)
  }

  const onCloseSectionOrderModal = (newInputs: any[] | null) => {
    if (!orderPageIndex) return
    if (!newInputs) {
      setOrderPageIndex(null)
      return
    }

    const { fieldOrigin, sectionId } = orderPageIndex
    const index = data?.sections?.[fieldOrigin]?.findIndex((v) => v.id == sectionId)
    const fields = newInputs?.map((v) => v.fieldKey)
    const newData = cloneDeep(data)
    if (
      index !== undefined &&
      index != -1 &&
      newData &&
      newData.sections &&
      newData.sections[fieldOrigin] &&
      newData.sections?.[fieldOrigin]?.[index]
    )
      (newData as any).sections[fieldOrigin][index].fields = fields

    setData(newData)
  }

  const onClosePageOrderModal = (newInputs: { title: string }[] | null) => {
    setOrderModal(false)
    if (!newInputs || !showOrderModal || !data) return

    const newData = cloneDeep(data)
    const newOrder: string[] = []
    const keys = Object.keys(pageTitlesOptions)
    const titles = Object.values(pageTitlesOptions)

    newInputs.forEach((v) => {
      const index = titles.findIndex((s) => s == v.title)
      newOrder.push(keys[index])
    })
    newData.order = newOrder as any
    setData(newData)
  }

  const getSectionFields = (fieldOrigin: FieldOrigin, sectionId: number) => {
    const section = data?.sections?.[fieldOrigin]?.find((v) => v.id == sectionId)
    if (!section) return []
    return section.fields
  }

  const getAllSectionFields = (fieldOrigin: FieldOrigin) => {
    const allFields: string[] = []
    data?.sections?.[fieldOrigin]?.forEach((v) => allFields.push(...v.fields))
    return allFields
  }

  const renderInput = (
    fieldOrigin: FieldOrigin,
    fieldKey: string,
    input: InputType,
    isCustomInput: boolean = false,
  ) => {
    if (!data) return null
    const itemData = data[fieldOrigin] && data[fieldOrigin][fieldKey] ? data[fieldOrigin][fieldKey] : {}
    if (!isCustomInput && fieldKey == `${Number(fieldKey)}`) isCustomInput = true

    const visible = itemData.visible || itemData.visible === undefined
    let defaultValue = itemData.defaultValue
    if (defaultValue === undefined) defaultValue = ''
    const tooltip = itemData.tooltip || ''
    const visibleLogicText = getVisibleLogics2Text(itemData, pageTitlesOptions, inputs)
    if (isCustomInput) {
      input.title = itemData.title || input.title
      // if (itemData.linkedField) input.title = `${input.title} (Linked)`
    }

    if (itemData.required !== undefined) input.required = itemData.required

    if (itemData.type == 'percentOfLoanAmount') input.readOnly = true

    if (
      data[fieldOrigin]?.[fieldKey]?.options?.length &&
      [...loanCustomRangeOptionFields, ...loanCustomSelectOptionFields].includes(fieldKey)
    ) {
      const options = convertSelectRangeOptionsToMap(data[fieldOrigin][fieldKey]?.options || [])
      if (options && Object.keys(options).length) (input as any).options = options
    }
    // Preview what the loan will show, which for these fields is Loan Structure's list however the
    // section was configured, rather than a section copy the loan no longer reads.
    const renamed = customOptionLabels(
      editableFieldOptions(fieldKey, data[fieldOrigin], data[FieldOrigin.LoanStructure]),
    )
    if (renamed) (input as any).options = renamed

    input.type = (itemData.type || input.type) as any
    const RequiredIcon = input.required ? BoltIcon : BoltSlashIcon

    const fieldForRequiredUi = {
      required: itemData.required !== undefined ? itemData.required : input.required,
      coborrowerRequired: itemData.coborrowerRequired,
    }
    const anyBorrowerRequired = hasAnyBorrowerRequired(fieldForRequiredUi)
    const PerBorrowerRequiredIcon = anyBorrowerRequired ? BoltIcon : BoltSlashIcon

    const showVisibleBtn = canEditField(fieldOrigin, fieldKey)
    const showEditBtn = canEditField(fieldOrigin, fieldKey)
    const defaultValueEditable = true // fieldOrigin != FieldOrigin.TrackRecord

    let additionalElements = (
      <div className="flex">
        {tooltip && (
          <Tooltip message={tooltip}>
            <span className="btn-icon">
              <QuestionMarkCircleIcon className="w-4 h-4 text-gray-500" />
            </span>
          </Tooltip>
        )}
        {showVisibleBtn && (
          <Tooltip message={visibleLogicText}>
            <span className="btn-icon" onClick={() => onChange(fieldOrigin, fieldKey, 'visible', !visible, input)}>
              {visible ? (
                <EyeIcon className={`w-4 h-4 ${!visibleLogicText && 'text-gray-500'}`} />
              ) : (
                <EyeSlashIcon className={`w-4 h-4 ${!visibleLogicText && 'text-gray-500'}`} />
              )}
            </span>
          </Tooltip>
        )}

        {showEditBtn && (
          <span className="btn-icon" onClick={() => onShowProperty(fieldOrigin, fieldKey, input)}>
            <PencilSquareIcon className="w-4 h-4 text-gray-500" />
          </span>
        )}

        {(fieldOrigin === ('sign' as FieldOrigin) || fieldOrigin === ('credit' as FieldOrigin)) &&
          fieldKey === 'template' && (
            <Tooltip
              message={
                fieldOrigin === ('sign' as FieldOrigin) ? 'Edit acknowledgement template' : 'Edit credit score template'
              }
            >
              <span
                className="btn-icon"
                onClick={() =>
                  setTemplateContentModal({ fieldOrigin: fieldOrigin as unknown as 'sign' | 'credit', fieldKey })
                }
              >
                <DocumentTextIcon className="w-4 h-4 text-gray-500" />
              </span>
            </Tooltip>
          )}

        {fieldOrigin === ('sign' as FieldOrigin) && fieldKey === 'originatorInformation' && (
          <Tooltip message="Edit loan originator labels">
            <span className="btn-icon" onClick={() => setOriginatorConfigModal(true)}>
              <DocumentTextIcon className="w-4 h-4 text-gray-500" />
            </span>
          </Tooltip>
        )}

        {hasCoborrowerRequiredProps(fieldOrigin) ? (
          <Tooltip
            message={
              anyBorrowerRequired
                ? 'At least one borrower (1–8) must complete this field. Click to edit.'
                : 'No borrower is required. Click to choose which borrowers (1–8) must complete this field.'
            }
          >
            <span className="btn-icon" onClick={() => setCoborrowerFieldModal({ fieldOrigin, fieldKey, input })}>
              <PerBorrowerRequiredIcon className="w-4 h-4 text-gray-500" />
            </span>
          </Tooltip>
        ) : (
          hasRequiredProps(fieldOrigin) &&
          !canControlSection(fieldOrigin, fieldKey) && (
            <Tooltip message={input.required ? 'Required' : 'Not Required'}>
              <span
                className="btn-icon"
                onClick={() => onChange(fieldOrigin, fieldKey, 'required', !input.required, input)}
              >
                <RequiredIcon className="w-4 h-4 text-gray-500" />
              </span>
            </Tooltip>
          )
        )}

        {isCustomInput && (
          <span className="btn-icon" onClick={() => onRemoveCustomInput(fieldOrigin, fieldKey)}>
            <TrashIcon className="w-4 h-4 text-red-500" />
          </span>
        )}

        {['togglebutton', 'check', 'checkbox'].includes(input.inputType?.toLowerCase()) && defaultValue !== '' && (
          <Tooltip message="Reset default value">
            <span
              className="btn-icon"
              onClick={() => onChange(fieldOrigin, fieldKey, 'defaultValue', undefined, input)}
            >
              <XCircleIcon className="w-4 h-4 text-gray-500" />
            </span>
          </Tooltip>
        )}
      </div>
    )

    if (input.inputType == 'Section' && !canControlSection(fieldOrigin, fieldKey)) additionalElements = <></>

    if (fieldOrigin == FieldOrigin.DeclarationsHMDA && ['1', '2'].includes(itemData.section || '')) {
      input.span = 'full'
      if (input.inputType?.toLowerCase() == 'togglebutton') (input as any).textLeft = true
    }

    let additionalProps: Record<string, any> = {}
    if (input.inputType == 'FileTable') additionalProps.disabled = true

    if ((input.inputType as any) == 'ActionButton') {
      const anyInput = input as any
      const buttonStyle = (itemData as any).buttonStyle || anyInput.buttonStyle || 'primary'
      const actionType = (itemData as any).actionType || anyInput.actionType
      const actionKey = (itemData as any).actionKey || anyInput.actionKey || fieldKey
      return (
        <React.Fragment key={`${fieldOrigin}-${fieldKey}`}>
          <div
            className={`grid grid-cols-1 items-center ${visible ? 'opacity-100' : 'opacity-30 z-10'} ${
              anyInput.span ? `col-span-${anyInput.span}` : ''
            } mb-2`}
            key={`${fieldOrigin}-${fieldKey}`}
          >
            <div className="flex flex-col items-center gap-3">
              <Button
                outline={buttonStyle === 'outline'}
                link={buttonStyle === 'link'}
                disabled
                className="pointer-events-none"
              >
                {anyInput.title}
              </Button>
              <span className="text-xs text-gray-500">
                Action: {actionType || '-'} / {actionKey || '-'}
              </span>
              <div className="ml-auto">{additionalElements}</div>
            </div>
          </div>
        </React.Fragment>
      )
    }

    const inputTypeLower = String(input.inputType || '').toLowerCase()
    // User-created custom fields and Google Map have no editor here, so they
    // show a label preview. Track Record rules already render a Yes/No plus
    // the number the pass/fail check uses — keep that editor.
    const keepOwnRender = fieldOrigin === FieldOrigin.TrackRecord && typeof (input as any).render === 'function'
    const isCustomPreview = (inputTypeLower === 'custom' || inputTypeLower === 'googlemap') && !keepOwnRender
    if (isCustomPreview) {
      ;(input as any).render = (_value: any, _onChange: Function, previewInput: InputType) => (
        <CustomFieldPreview title={previewInput.title} additionalElements={previewInput.additionalElements} />
      )
    }

    return (
      <React.Fragment key={`${fieldOrigin}-${fieldKey}`}>
        <div
          className={`grid grid-cols-1 items-center ${visible ? 'opacity-100' : 'opacity-30 z-10'} ${
            input.span ? `col-span-${input.span}` : ''
          } mb-2`}
          key={`${fieldOrigin}-${fieldKey}`}
        >
          <RenderInput
            key={`${fieldOrigin}-${fieldKey}`}
            input={
              {
                ...input,
                ...(isCustomPreview ? { inputType: 'custom' } : {}),
                value: defaultValue,
                additionalElements,
                ...additionalProps,
              } as InputType
            }
            Key={`${fieldOrigin}-${fieldKey}`}
            onChange={(inputKey: string, value: any) => {
              itemData.visible !== false &&
                defaultValueEditable &&
                onChange(fieldOrigin, fieldKey, 'defaultValue', value, input)
            }}
          />
        </div>
      </React.Fragment>
    )
  }

  const getSectionOptions = (fieldOrigin: FieldOrigin) => {
    if (!data) return null
    if (!data.sections || !data.sections[fieldOrigin]) return null
    const sections = data.sections![fieldOrigin]!
    const options: Record<string, string> = {}
    sections.forEach((section) => {
      ;(options as any)[`${section.id}`] = section.title
    })
    return options
  }

  const renderSectionFields = (fieldOrigin: FieldOrigin, sectionId: number | null) => {
    if (!data || !inputs[fieldOrigin]) return null

    const sectionFields = sectionId ? getSectionFields(fieldOrigin, sectionId) : getAllSectionFields(fieldOrigin)

    return (
      <>
        {sectionId == null && !!sectionFields.length && <Section title="No Section" />}
        <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2`}>
          {(sectionId ? sectionFields : Object.keys(inputs[fieldOrigin])).map((fieldKey: string) => {
            const item = inputs[fieldOrigin][fieldKey]
            if (!item) return

            if (
              !canHaveSection(fieldOrigin) ||
              (sectionId && sectionFields.includes(fieldKey)) ||
              (!sectionId && !sectionFields.includes(fieldKey))
            )
              return renderInput(fieldOrigin, fieldKey, item, isCustomOrigin(fieldOrigin))
            return null
          })}
        </div>
      </>
    )
  }

  const onAddNewSection = (fieldOrigin: FieldOrigin) => {
    if (!data) return null

    const newData = cloneDeep(data)
    if (!newData.sections) newData.sections = {}
    if (!newData.sections[fieldOrigin]) newData.sections[fieldOrigin] = []
    newData.sections[fieldOrigin]!.push({
      id: Date.now(),
      title: `Section ${newData.sections[fieldOrigin]!.length + 1}`,
      fields: [],
    })
    setData(newData)
  }

  const onAddNewField = (
    fieldOrigin: FieldOrigin,
    defaultFieldData: Record<string, any> = { section: '' },
    defaultPropData: Record<string, any> = {},
  ) => {
    setSelectedField({
      fieldOrigin,
      fieldKey: '',
      defaultFieldData,
      defaultPropData,
    })
  }

  const renderAdditionalInputs = (fieldOrigin: FieldOrigin) => {
    if (!data) return null
    const additionalInputs = data.properties?.[fieldOrigin]?.additionalInputs || {}

    const onUpdateAdditionalInputs = (newInputs: any) => {
      const newData = cloneDeep(data)
      if (!newData.properties) newData.properties = {} as any
      if (!newData.properties?.[fieldOrigin]) (newData.properties as any)[fieldOrigin] = {} as any
      ;(newData.properties as any)[fieldOrigin].additionalInputs = newInputs
      setData(newData)
      setChanged(true)
    }

    switch (fieldOrigin) {
      case FieldOrigin.LoanStructure:
        return (
          <BaseAdditionalInputs
            loanFieldProps={data}
            data={additionalInputs}
            fieldTitles={fieldTitles}
            fieldInputs={inputs}
            onUpdate={onUpdateAdditionalInputs}
            title="Additional Properties"
            fieldOrigin={FieldOrigin.LoanStructure}
            propertyKey="additionalProperties"
          />
        )

      case FieldOrigin.BorrowerInformation:
        return (
          <BaseAdditionalInputs
            loanFieldProps={data}
            data={additionalInputs}
            fieldTitles={fieldTitles}
            fieldInputs={inputs}
            onUpdate={onUpdateAdditionalInputs}
            title="Additional Layer"
            fieldOrigin={FieldOrigin.BorrowerInformation}
            propertyKey="additionalLayer"
          />
        )

      default:
        return null
    }
  }

  const renderFieldOriginSection = (fieldOrigin: FieldOrigin) => {
    if (!data) return null

    let sections: ILoanOriginSection[] = []
    if (data.sections && data.sections[fieldOrigin]) {
      sections = data.sections[fieldOrigin] || []
    }

    const onUpdateSectionTitle = async (section: ILoanOriginSection, index: number) => {
      const title: any = await prompt('Input section title', { value: section.title })
      if (title === false) return

      const newData = cloneDeep(data)
      const sections = newData.sections![fieldOrigin]!
      sections[index].title = title
      setData(newData)
    }

    const onRemoveSection = async (section: ILoanOriginSection, index: number) => {
      const isContinue = await confirm('Are you sure you want to remove this section?')
      if (!isContinue) return

      const newData = cloneDeep(data)
      newData?.sections?.[fieldOrigin]?.splice(index, 1)
      setData(newData)
    }

    const onUpdateOrder = (index: number, direction: number) => {
      const newData = cloneDeep(data)
      const sections = newData.sections![fieldOrigin]!
      sections.splice(index + direction, 0, sections.splice(index, 1)[0])
      setData(newData)
    }

    return (
      <div>
        {canHaveSection(fieldOrigin) &&
          sections.map((section, index) => {
            return (
              <div key={section.id} className="mb-4">
                <RenderInput
                  Key={`section-${index}`}
                  input={{
                    inputType: 'section',
                    title: section.title || '',
                    additionalElements: (
                      <div className="flex gap-2">
                        <Tooltip message="Add a new field in this section">
                          <span
                            className="btn-icon"
                            onClick={() => onAddNewField(fieldOrigin, { section: `${section.id}` })}
                          >
                            <PlusIcon className="w-4 h-4" />
                          </span>
                        </Tooltip>

                        {canAddLinkedFieldOrigin(fieldOrigin) && (
                          <Tooltip message="Add a new linked field in this section">
                            <span
                              className="btn-icon"
                              onClick={() =>
                                onAddNewField(fieldOrigin, { section: `${section.id}` }, { linkedField: {} })
                              }
                            >
                              <LinkIcon className="w-4 h-4" />
                            </span>
                          </Tooltip>
                        )}
                        <p
                          className="btn-icon"
                          onClick={() => setOrderPageIndex({ fieldOrigin, sectionId: section.id })}
                        >
                          <BarsArrowDownIcon className="w-4 h-4" />
                        </p>

                        {index != 0 && (
                          <span className="btn-icon" onClick={() => onUpdateOrder(index, -1)}>
                            <ArrowUpIcon className="w-4 h-4" />
                          </span>
                        )}
                        {index != sections.length - 1 && (
                          <span className="btn-icon" onClick={() => onUpdateOrder(index, 1)}>
                            <ArrowDownIcon className="w-4 h-4" />
                          </span>
                        )}

                        <span className="btn-icon" onClick={() => onUpdateSectionTitle(section, index)}>
                          <PencilSquareIcon className="w-4 h-4" />
                        </span>
                        <span className="btn-icon !text-red-500" onClick={() => onRemoveSection(section, index)}>
                          <TrashIcon className="w-4 h-4" />
                        </span>
                      </div>
                    ),
                  }}
                  onChange={() => {}}
                />
                {renderSectionFields(fieldOrigin, section.id)}
              </div>
            )
          })}

        {renderSectionFields(fieldOrigin, null)}

        <div className="flex items-center gap-4">
          {canHaveSection(fieldOrigin) && (
            <Button link onClick={() => onAddNewSection(fieldOrigin)}>
              + Add New Section
            </Button>
          )}
          {canAddNewField(fieldOrigin) && (
            <Button link onClick={() => onAddNewField(fieldOrigin)}>
              + Add New Field
            </Button>
          )}
          {canAddLinkedFieldOrigin(fieldOrigin) && (
            <Button link onClick={() => onAddNewField(fieldOrigin, {}, { linkedField: {} })}>
              + Add New Linked Field
            </Button>
          )}
        </div>

        {renderAdditionalInputs(fieldOrigin)}
      </div>
    )
  }

  const renderParagraph = (_fieldOrigin: string, pageIdx: number) => {
    const fieldOrigin = _fieldOrigin as FieldOrigin
    const isVisible = isVisibleSection(fieldOrigin)
    const VisibleIcon = isVisible ? EyeIcon : EyeSlashIcon

    return (
      <div className={`${isVisible ? '' : 'opacity-50'}`} key={`${_fieldOrigin}-${pageIdx}`}>
        <div className="flex items-center justify-between border-b mb-2">
          <p className={`text-lg font-bold flex items-center gap-4 ${isVisible ? '' : 'line-through'}`}>
            {pageIdx + 1}.{' '}
            {editTitleID === fieldOrigin ? (
              <>
                <input
                  className="w-60 border-black border-2 rounded-md px-2"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                ></input>
                <span className="text-red-600 cursor-pointer hover-shadow1 p-1 rounded" onClick={onCloseEdit}>
                  <XMarkIcon className="w-4 h-4"></XMarkIcon>
                </span>
                <span className="btn-icon" onClick={onOkEdit}>
                  <CheckIcon className="w-4 h-4"></CheckIcon>
                </span>
              </>
            ) : (
              <>{customTitle(fieldOrigin)}</>
            )}
            {isShowVisibleSection(fieldOrigin) && (
              <>
                {editTitleID !== fieldOrigin && (
                  <span className="cursor-pointer hover-shadow1 p-1 rounded" onClick={() => onEditTitle(fieldOrigin)}>
                    <PencilSquareIcon className="w-4 h-4" />
                  </span>
                )}
                <span className="btn-icon" onClick={() => onVisibleSection(fieldOrigin)}>
                  <VisibleIcon className="w-4 h-4" />
                </span>

                <Tooltip message="Visible by account types and channel">
                  <span className="btn-icon" onClick={() => onShowAccountModal(fieldOrigin)}>
                    <UsersIcon className="w-4 h-4" />
                  </span>
                </Tooltip>

                {isCustomOrigin(fieldOrigin) && editTitleID !== fieldOrigin && (
                  <span className="btn-icon !text-red-500" onClick={() => onRemoveOrigin(fieldOrigin)}>
                    <TrashIcon className="w-4 h-4" />
                  </span>
                )}
              </>
            )}
          </p>
          <div className="flex items-center gap-2">
            {canAddNewField(fieldOrigin) && (
              <Tooltip message="Add new field">
                <span className="btn-icon" onClick={() => onAddNewField(fieldOrigin)}>
                  <PlusIcon className="w-4 h-4" />
                </span>
              </Tooltip>
            )}

            {canAddLinkedFieldOrigin(fieldOrigin) && (
              <Tooltip message="Add new linked field">
                <span className="btn-icon" onClick={() => onAddNewField(fieldOrigin, {}, { linkedField: {} })}>
                  <LinkIcon className="w-4 h-4" />
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        {renderFieldOriginSection(fieldOrigin)}
      </div>
    )
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <Prompt when={isChanged} message={`You've made some changes!\nAre you sure want to leave without Saving?`} />
      <div>{[FieldOrigin.LoanStructure, ...Object.keys(pageTitlesOptions)].map(renderParagraph)}</div>
      <div className="flex flex-row justify-center gap-4">
        <Button onClick={onAddNewPage} loading={isLoading} outline>
          Add new Page
        </Button>
        <Button onClick={() => setOrderModal(true)} loading={isLoading} outline>
          Update Order
        </Button>
        <Button onClick={onSubmit} loading={isLoading} disabled={!isChanged}>
          Save
        </Button>
      </div>
      <SaveChanges show={isChanged} label="Save Changes" onSave={onSubmit} />

      {selectedField && selectedFieldData && data && (
        <VisiblePropertyModal
          field={selectedField}
          data={selectedFieldData}
          sectionOptions={getSectionOptions(selectedField.fieldOrigin)}
          inputProps={inputs[selectedField.fieldOrigin]?.[selectedField.fieldKey] || {}}
          onClose={onCloseVisiblePropertyModal}
          fieldTitles={fieldTitles}
          fieldInputs={inputs}
          actionTypeOptions={actionTypeOptions}
          loanFieldProps={data}
        />
      )}
      {coborrowerFieldModal && data && (
        <CoborrowerRequiredModal
          key={`${coborrowerFieldModal.fieldOrigin}-${coborrowerFieldModal.fieldKey}`}
          fieldOrigin={coborrowerFieldModal.fieldOrigin}
          fieldKey={coborrowerFieldModal.fieldKey}
          itemData={data[coborrowerFieldModal.fieldOrigin]?.[coborrowerFieldModal.fieldKey] || {}}
          input={coborrowerFieldModal.input}
          onClose={() => setCoborrowerFieldModal(null)}
          onSave={onCoborrowerRequiredModalSave}
        />
      )}
      {templateContentModal && data && (
        <TemplateContentModal
          kind={templateContentModal.fieldOrigin === 'credit' ? 'credit' : 'sign'}
          value={(data[templateContentModal.fieldOrigin]?.[templateContentModal.fieldKey] as any)?.defaultValue}
          onClose={() => setTemplateContentModal(null)}
          onSave={onSaveTemplateContent}
        />
      )}
      {originatorConfigModal && data && (
        <OriginatorConfigModal
          value={(data.sign?.originatorInformation as any)?.defaultValue}
          onClose={() => setOriginatorConfigModal(false)}
          onSave={onSaveOriginatorConfig}
        />
      )}
      {orderPageIndex != null && (
        <PageOrderModal key="sectionOrder" inputs={orderingSectionInputs} onClose={onCloseSectionOrderModal} />
      )}
      {showOrderModal && data && (
        <PageOrderModal
          key="pageOrder"
          inputs={Object.values(pageTitlesOptions).map((value) => ({ title: value }))}
          onClose={onClosePageOrderModal}
        />
      )}
      {modalFieldOrigin && (
        <VisibleSectionModal
          onClose={onCloseAccountModal}
          onOk={onOkAccountModal}
          data={getPageVisibleProps(modalFieldOrigin)}
          fieldOrigin={modalFieldOrigin}
        />
      )}
    </div>
  )
}
